import React, { useState } from 'react';

import { Alert, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';

import type { AiCodeValidationIssue as ValidationIssue } from '~/services/PromoVoucherAiService';

type Props = {
  issue: ValidationIssue;
  disabled: boolean;
  onResolve: (answer: string) => void;
};

export default function AiCodeValidationIssue({ issue, disabled, onResolve }: Props) {
  const [answer, setAnswer] = useState('');
  const [selectedValues, setSelectedValues] = useState<Array<string>>([]);
  const prompt = issue.prompt;
  const options =
    prompt && (prompt.inputType === 'select' || prompt.inputType === 'multi-select') ? prompt.options : [];
  const multiple = prompt?.inputType === 'multi-select';
  const hasAnswer = multiple ? selectedValues.length > 0 : answer.trim().length > 0;

  return (
    <Stack spacing={1}>
      <Alert severity={issue.level}>{issue.fixSuggestion}</Alert>
      {issue.explanation && <Typography variant="body2">{issue.explanation}</Typography>}
      {prompt ? (
        <>
          <TextField
            label={prompt.label}
            select={options.length > 0}
            type={prompt.inputType === 'number' ? 'number' : 'text'}
            value={multiple ? selectedValues : answer}
            SelectProps={multiple ? { multiple: true } : undefined}
            onChange={(event) => {
              const value = event.target.value;
              if (multiple) {
                setSelectedValues(typeof value === 'string' ? value.split(',') : value);
              } else {
                setAnswer(value);
              }
            }}
            disabled={disabled}
            fullWidth
          >
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="contained"
            disabled={disabled || !hasAnswer}
            onClick={() =>
              onResolve(
                `${issue.check} (${prompt.key}) — ${prompt.label}: ${multiple ? JSON.stringify(selectedValues) : answer.trim()}`,
              )
            }
          >
            Apply answer and recheck
          </Button>
        </>
      ) : issue.availableOptions?.length ? (
        <>
          <Typography variant="subtitle2">Available options</Typography>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {issue.availableOptions.map((option) => (
              <Button
                key={option}
                variant="text"
                disabled={disabled}
                onClick={() => onResolve(`${issue.check}: ${option}`)}
              >
                {option}
              </Button>
            ))}
          </Stack>
        </>
      ) : (
        <Typography variant="body2">Reply above with the correction, then send to recheck.</Typography>
      )}
    </Stack>
  );
}
